'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { aiModelsApi, YoloResponse, YoloDetection, YoloClassification } from '@/lib/ai-models-api';

type YoloTask = 'detect' | 'segment' | 'pose' | 'classify';

export default function YoloPage() {
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [task, setTask] = useState<YoloTask>('detect');
  const [confidence, setConfidence] = useState(0.25);
  const [iou, setIou] = useState(0.45);
  const [modelVariant, setModelVariant] = useState('yolo11n');
  const [returnAnnotated, setReturnAnnotated] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<YoloResponse | null>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await aiModelsApi.constructor.prototype.constructor.fileToBase64
      ? await (aiModelsApi.constructor as any).fileToBase64(file)
      : await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.readAsDataURL(file);
        });

    setImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return;

    setLoading(true);
    setError(null);

    try {
      let response: YoloResponse;
      const params = {
        confidence,
        iou,
        model_variant: modelVariant,
        return_annotated: returnAnnotated,
      };

      switch (task) {
        case 'detect':
          response = await aiModelsApi.yoloDetect(image, params);
          break;
        case 'segment':
          response = await aiModelsApi.yoloSegment(image, params);
          break;
        case 'pose':
          response = await aiModelsApi.yoloPose(image, { confidence, model_variant: modelVariant, return_annotated: returnAnnotated });
          break;
        case 'classify':
          response = await aiModelsApi.yoloClassify(image, { model_variant: modelVariant });
          break;
        default:
          throw new Error('Unknown task');
      }

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">YOLO - Object Detection</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Task */}
                <div>
                  <label className="block text-sm font-medium mb-2">Task</label>
                  <select
                    value={task}
                    onChange={(e) => setTask(e.target.value as YoloTask)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="detect">Detection</option>
                    <option value="segment">Segmentation</option>
                    <option value="pose">Pose Estimation</option>
                    <option value="classify">Classification</option>
                  </select>
                </div>

                {/* Model Variant */}
                <div>
                  <label className="block text-sm font-medium mb-2">Model Variant</label>
                  <select
                    value={modelVariant}
                    onChange={(e) => setModelVariant(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="yolo11n">YOLO11n (Nano - Fastest)</option>
                    <option value="yolo11s">YOLO11s (Small)</option>
                    <option value="yolo11m">YOLO11m (Medium)</option>
                    <option value="yolo11l">YOLO11l (Large)</option>
                    <option value="yolo11x">YOLO11x (XLarge - Most Accurate)</option>
                  </select>
                </div>

                {/* Confidence */}
                {task !== 'classify' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Confidence: {confidence.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min="0.01"
                      max="1"
                      step="0.01"
                      value={confidence}
                      onChange={(e) => setConfidence(parseFloat(e.target.value))}
                      className="w-full accent-[var(--accent)]"
                    />
                  </div>
                )}

                {/* IoU */}
                {(task === 'detect' || task === 'segment') && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      IoU Threshold: {iou.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.05"
                      value={iou}
                      onChange={(e) => setIou(parseFloat(e.target.value))}
                      className="w-full accent-[var(--accent)]"
                    />
                  </div>
                )}

                {/* Return Annotated */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="returnAnnotated"
                    checked={returnAnnotated}
                    onChange={(e) => setReturnAnnotated(e.target.checked)}
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <label htmlFor="returnAnnotated" className="text-sm">
                    Return annotated image
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!image || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Run Detection'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Results</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image and run detection</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Processing...</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Annotated Image */}
                    {result.annotated_image && (
                      <div>
                        <p className="text-sm text-[var(--text-muted)] mb-2">Annotated Image</p>
                        <img
                          src={result.annotated_image.startsWith('data:') ? result.annotated_image : `data:image/jpeg;base64,${result.annotated_image}`}
                          alt="Annotated"
                          className="w-full rounded-lg"
                        />
                      </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">
                          {result.classifications ? 'Classifications' : 'Detections'}
                        </p>
                        <p className="text-lg font-semibold">
                          {result.classifications?.length ?? result.detections?.length ?? 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Image Size</p>
                        <p className="text-lg font-semibold">{result.image_size[0]}x{result.image_size[1]}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Inference</p>
                        <p className="text-lg font-semibold">{result.inference_time_ms.toFixed(0)}ms</p>
                      </div>
                    </div>

                    {/* Detections List */}
                    {result.detections && result.detections.length > 0 && (
                      <div>
                        <p className="text-sm text-[var(--text-muted)] mb-2">Detected Objects</p>
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {result.detections.map((det: YoloDetection, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-3 bg-[var(--bg-primary)] rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <span className="w-6 h-6 flex items-center justify-center bg-[var(--accent-soft)] text-[var(--accent)] rounded text-xs font-medium">
                                  {idx + 1}
                                </span>
                                <span className="font-medium">{det.class_name}</span>
                              </div>
                              <span className="text-sm text-[var(--text-muted)]">
                                {(det.confidence * 100).toFixed(1)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Classifications List */}
                    {result.classifications && result.classifications.length > 0 && (
                      <div>
                        <p className="text-sm text-[var(--text-muted)] mb-2">Top Classifications</p>
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {result.classifications.map((cls: YoloClassification, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-3 bg-[var(--bg-primary)] rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <span className="w-6 h-6 flex items-center justify-center bg-[var(--accent-soft)] text-[var(--accent)] rounded text-xs font-medium">
                                  {idx + 1}
                                </span>
                                <span className="font-medium">{cls.class_name}</span>
                              </div>
                              <span className="text-sm text-[var(--text-muted)]">
                                {(cls.confidence * 100).toFixed(1)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
